import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

// Tesla-style lineup: full-bleed light image + three category CTAs beneath.
export default function TeslaLineup({ onFilterCategory, selectedCategory = "" }) {
  const CATEGORIES = [
    { key: "autos", label: "Autos", sub: "Diario, elegante", category: "Autos" },
    { key: "camioneta", label: "Camionetas", sub: "Trabajo y aventura", category: "Camionetas" },
    { key: "suv", label: "SUV", sub: "Familia y estilo", category: "SUV" },
    { key: "van", label: "Van pasajeros", sub: "Flotilla y logística", category: "Van pasajeros" },
  ];

  return (
    <section
      className="relative bg-[#FFFFFF] text-[#0B1623] py-16 md:py-24"
      data-testid="tesla-lineup"
      aria-label="Nuestra flota"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12 md:mb-16">
          <span className="font-rajdhani font-medium text-[#3B82F6] text-xs md:text-sm tracking-[0.4em] uppercase">
            Nuestra Flota
          </span>
          <h2 className="font-rajdhani font-semibold text-[#0B1623] tracking-tight leading-[0.95] text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-4">
            Un modelo para
            <br />
            cada camino.
          </h2>
        </div>

        {/* Lineup image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          className="relative rounded-2xl overflow-hidden bg-white/60"
        >
          <img
            src="/mockups/v2_03_tesla_style_lineup.jpg"
            alt="Nissan Sentra, Frontier y Urvan"
            className="w-full h-auto object-cover"
          />
        </motion.div>

        {/* Chip "Todos" — reset del filtro, siempre visible. Activo cuando no hay categoría seleccionada. */}
        <div className="mt-10 md:mt-14 flex justify-center">
          <button
            onClick={() => onFilterCategory?.("")}
            className={`inline-flex items-center gap-2 px-6 md:px-8 py-2.5 md:py-3 rounded-full font-rajdhani font-semibold text-sm md:text-base tracking-[0.28em] uppercase border transition-all duration-300 ${
              !selectedCategory
                ? "bg-[#0B1623] text-white border-[#0B1623] shadow-[0_8px_22px_rgba(11,22,35,0.18)]"
                : "bg-white text-[#0B1623] border-[#0B1623]/15 hover:border-[#0B1623]/45 hover:shadow-[0_8px_18px_rgba(11,22,35,0.08)]"
            }`}
            data-testid="lineup-cta-todos"
          >
            <span>Todos</span>
            <span className={`text-xs font-manrope font-normal ${!selectedCategory ? "text-white/70" : "text-[#0B1623]/50"}`}>
              {selectedCategory ? "(mostrar todo)" : "(activo)"}
            </span>
          </button>
        </div>

        {/* Category CTAs — grid ajustado a 4 categorías */}
        <div className="mt-6 md:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
          {CATEGORIES.map((c, idx) => {
            const isActive = (selectedCategory || "").toLowerCase() === c.category.toLowerCase();
            return (
              <motion.button
                key={c.key}
                onClick={() => onFilterCategory?.(c.category)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                whileHover={{ y: -4 }}
                className={`group text-left rounded-2xl border p-6 md:p-7 transition-all duration-500 ${
                  isActive
                    ? "bg-[#3B82F6]/8 border-[#3B82F6] shadow-[0_16px_40px_rgba(59,130,246,0.14)]"
                    : "bg-white border-[#0B1623]/8 hover:border-[#0B1623]/25 shadow-[0_2px_8px_rgba(11,22,35,0.04)] hover:shadow-[0_12px_32px_rgba(11,22,35,0.08)]"
                }`}
                data-testid={`lineup-cta-${c.key}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-rajdhani font-semibold text-[#0B1623] text-xl md:text-2xl tracking-tight">
                      {c.label}
                    </p>
                    <p className="font-manrope text-[#0B1623]/60 text-sm mt-1">{c.sub}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                    isActive ? "bg-[#3B82F6] text-white" : "bg-[#0B1623]/5 group-hover:bg-[#F09A21] group-hover:text-white"
                  }`}>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
